import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  buildRegistrationHtml, clientRegistration, registrationFileName, vendorRegistration, type RegistrationDoc,
} from '../lib/registrationForm';

const btn = {
  padding: '6px 16px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13,
} as const;

/**
 * The registration form on an A4 sheet, with Print (the browser's dialog
 * also saves a PDF) and Download PDF.  /vendors/blank/print and
 * /clients/blank/print give the empty form to fill in by hand.
 */
function RegistrationSheet({ doc }: { doc: RegistrationDoc }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileName = registrationFileName(doc);

  // The browser's "Save as PDF" names the file after the page title.
  useEffect(() => {
    const before = document.title;
    document.title = fileName.replace(/\.pdf$/, '');
    return () => { document.title = before; };
  }, [fileName]);

  async function downloadPdf() {
    setBusy(true);
    setError(null);
    try {
      const { generateRegistrationPdf } = await import('../lib/generateRegistrationPdf');
      await generateRegistrationPdf(doc);
    } catch (e) {
      setError(`PDF failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          .reg-desk { background: none !important; padding: 0 !important; min-height: 0 !important; overflow: visible !important; }
          .reg-sheet { width: auto !important; padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ padding: '12px 24px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid #ddd', flexWrap: 'wrap' }}>
        <button onClick={() => window.print()} style={{ ...btn, background: '#0f4c75', color: '#fff' }}>Print</button>
        <button onClick={downloadPdf} disabled={busy} style={{ ...btn, background: '#0f4c75', color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Preparing PDF…' : 'Download PDF'}
        </button>
        <button onClick={() => window.history.back()} style={{ ...btn, background: '#e2e8f0' }}>Back</button>
        {error
          ? <span style={{ color: '#b91c1c', fontSize: 13 }}>{error}</span>
          : <span style={{ color: '#64748b', fontSize: 12 }}>Empty boxes print blank, to fill in by hand.</span>}
      </div>

      <div className="reg-desk" style={{ background: '#e2e8f0', minHeight: '100vh', padding: '16px 0', overflowX: 'auto' }}>
        <div
          className="reg-sheet"
          style={{ width: '210mm', margin: '0 auto', background: '#fff', padding: '10mm', boxShadow: '0 1px 4px rgba(0,0,0,.15)' }}
          dangerouslySetInnerHTML={{ __html: buildRegistrationHtml(doc) }}
        />
      </div>
    </>
  );
}

function RecordSheet({ kind }: { kind: 'vendor' | 'client' }) {
  const { id = '' } = useParams();
  const blank = id === 'blank';
  const q = useQuery({
    enabled: !blank,
    queryKey: [`${kind}-print`, id],
    queryFn: () => api<Record<string, unknown>>(`/api/${kind}s/${encodeURIComponent(id)}`),
  });
  const toDoc = kind === 'vendor' ? vendorRegistration : clientRegistration;

  if (blank) return <RegistrationSheet doc={toDoc({})} />;
  if (q.isLoading) return <div style={{ padding: 24, color: '#666' }}>Loading…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 24, color: 'red' }}>Failed to load {kind}.</div>;
  return <RegistrationSheet doc={toDoc(q.data)} />;
}

export function VendorPrintPage() {
  return <RecordSheet kind="vendor" />;
}

export function ClientPrintPage() {
  return <RecordSheet kind="client" />;
}
